import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { FaFileInvoiceDollar, FaPrint } from 'react-icons/fa6';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { Loader } from '../../../components/loader/SpinnerLogo';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useHandleDownloadDocument from '../../../hooks/useDownloadDocument';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { setSelectedView } from '../../../redux/invoiceProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { InvoiceService } from '../../../services/invoice/invoiceService';
import { InvoiceApiResponse } from '../../../type/entities/invoice-type';
import { INVOICE_TYPES } from '../list/InvoicesList';
import {ArrowBack} from "@mui/icons-material";
import InvoiceRectificationModal from './components/InvoiceRectificationModal';

type InvoiceProfileLocationState = {
    fromContract?: boolean;
    returnToContractPath?: string;
};

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.invoices.profile.info,
        permission: {
            group: 'invoices',
            action: 'get_invoices',
        },
    },
    {
        label: 'Actividad',
        path: menuRoutes.invoices.profile.activityHistory,
        permission: {
            group: 'invoices',
            action: 'invoice_changes_log',
        },
    },
    {
        label: 'Vencimientos',
        path: menuRoutes.invoices.profile.expirations,
        permission: {
            group: 'expirations',
            action: 'admin_expirations',
        },
    }
];

export default function InvoiceProfileLayout({ isLoading, children }: { isLoading?: boolean, children: React.ReactNode }) {

    const { selectedView } = useSelector((state: RootState) => state.invoiceProfile);
    const { id = '' } = useParams<{ id: string }>();
    const location = useLocation();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const { handleErrors } = useHandleErrors();
    const profileLocationState = (location.state as InvoiceProfileLocationState | null) ?? null;
    const showBackToContract = Boolean(profileLocationState?.fromContract && profileLocationState?.returnToContractPath);
    const [openRectificationModal, setOpenRectificationModal] = useState(false);

    const [data, , , refetchInvoice] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const service = new InvoiceService();
        const response = await service.getInvoiceById(id);
        return response.getResponseData() as InvoiceApiResponse;
    }, [id]));

    const invoiceData = (data as any)?.data ?? data;
    const canDownloadInvoice = userCan('print_invoices', 'invoices');
    const canRectifyInvoice = Boolean(invoiceData?.id) && userCan('edit_invoices', 'invoices') && !invoiceData?.isRectificationInvoice;

    const pageHeader = {
        title: `Información de la factura ${invoiceData?.number || ''} (${INVOICE_TYPES.find(type => type.value === invoiceData?.invoiceType)?.label || ''})`,
        breadcrumb: [

            { name: 'Ventas' },
            { name: 'Gestión de facturas' },
            { name: 'Facturación', href: menuRoutes.invoices.path },
            { name: 'Datos de la factura' },
        ],
    };

    const handleDelete = async (id: string) => {
        try {
            const response = await (await (new InvoiceService()).deleteInvoice(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.invoices.path);
                toast.success('Factura eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    // TODO: Esto hay que cambiarlo, ya que no se puede hacer un navigate en un useEffect. Además se está cargando el sistema de filtros.
    useEffect(() => {
        setTimeout(() => {
            navigate(`${menuRoutes.invoices.path}/${id}/profile${selectedView}`, { state: location.state });
        }, 300);
        
    }, [selectedView]);

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {showBackToContract && (
                        <Button
                            variant={'outline'}
                            size={'md'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigate(profileLocationState?.returnToContractPath || menuRoutes.contracts.path)}
                        >
                            <ArrowBack/> Volver al contrato
                        </Button>
                    )}

                    {canDownloadInvoice && (
                        <Tooltip content='Descargar factura' placement='top' color='invert' size='sm'>
                            <Button
                                variant='outline'
                                color='primary'
                                size='md'
                                onClick={() => handleDownloadDocumentNewTab((new InvoiceService()).printInvoice, invoiceData?.id, moment(invoiceData?.invoiceDate?.date).format('DD-MM-YYYY') + '_factura.pdf')}
                            >
                                <FaPrint className="h-4 w-4" size={20} />
                            </Button>
                        </Tooltip>
                    )}

                    {canRectifyInvoice && (
                        <Tooltip content='Rectificar factura' placement='top' color='invert' size='sm'>
                            <Button
                                variant='outline'
                                color='primary'
                                size='md'
                                onClick={() => setOpenRectificationModal(true)}
                            >
                                <FaFileInvoiceDollar className="h-4 w-4" size={20} />
                            </Button>
                        </Tooltip>
                    )}

                    {userCan('edit_invoices', 'invoices') && (
                        <>
                            <Tooltip content={'Editar factura'} placement={'top'} color={'invert'} size={'sm'}>
                                <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                    navigate(`${menuRoutes.invoices.path}/${id}/edit`)
                                }}>
                                    Editar
                                </Button>
                            </Tooltip>
                        </>
                    )}

                    {userCan('delete_invoices', 'invoices') && (
                        <Tooltip content={'Eliminar factura'} placement={'top'} color={'invert'} size={'sm'} className='text-danger'>
                            <DeletePopover
                                title={`Eliminar factura`}
                                description={`¿Estás seguro de que deseas eliminar la factura?`}
                                size={'md'}
                                onDelete={() => handleDelete(id)}
                                actionIconClassName='h-auto w-10'
                            />
                        </Tooltip>
                    )}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <Loader height='60vh' /> : children}

            <InvoiceRectificationModal
                isOpen={openRectificationModal}
                invoice={invoiceData}
                onClose={() => setOpenRectificationModal(false)}
                onCompleted={refetchInvoice}
            />
        </>
    );
}
